import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Button,
  Input,
  Label,
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui';
import { PasswordInput } from './PasswordInput';
import { useToast } from '../ui/toast';
import type { StaffSummary } from '@leal-crm/shared';

interface StaffFormData {
  email: string;
  name: string;
  role: 'admin' | 'reception' | 'workshop';
  password: string;
  sendWelcomeEmail: boolean;
}

interface StaffFormProps {
  initialData?: Partial<StaffSummary>;
  staffId?: string;
  onSuccess: (staff: StaffSummary) => void;
  onCancel: () => void;
}

export function StaffForm({ initialData, staffId, onSuccess, onCancel }: StaffFormProps) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<StaffFormData>({
    email: initialData?.email || '',
    name: initialData?.name || '',
    role: initialData?.role || 'reception',
    password: '',
    sendWelcomeEmail: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof StaffFormData, string>>>({});

  const isEditing = Boolean(staffId);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof StaffFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    } else if (formData.name.length < 2) {
      newErrors.name = t('validation.minLength', { min: 2 });
    }

    if (!isEditing) {
      if (!formData.email.trim()) {
        newErrors.email = t('validation.required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('validation.invalidEmail');
      }

      if (!formData.password) {
        newErrors.password = t('validation.required');
      } else if (formData.password.length < 8) {
        newErrors.password = t('auth.passwordRequirements');
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
        newErrors.password = t('auth.passwordRequirements');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (errors[name as keyof StaffFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRoleChange = (value: string) => {
    setFormData((prev) => ({ ...prev, role: value as StaffFormData['role'] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      let response: { staff: StaffSummary };

      if (isEditing) {
        response = await api.patch<{ staff: StaffSummary }>(`/api/admin/staff/${staffId}`, {
          name: formData.name,
          role: formData.role,
        });
        addToast({ variant: 'success', title: t('staff.updated') });
      } else {
        response = await api.post<{ staff: StaffSummary }>('/api/admin/staff', {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          password: formData.password,
          sendWelcomeEmail: formData.sendWelcomeEmail,
        });
        addToast({ variant: 'success', title: t('staff.created') });
      }

      onSuccess(response.staff);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      addToast({ variant: 'destructive', title: message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label={t('staff.name')} error={errors.name} required>
        <Input
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Juan Garcia"
        />
      </FormField>

      {!isEditing && (
        <FormField label={t('staff.email')} error={errors.email} required>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="empleado@email.com"
          />
        </FormField>
      )}

      <div className="space-y-2">
        <Label htmlFor="role">{t('staff.role')}</Label>
        <Select value={formData.role} onValueChange={handleRoleChange}>
          <SelectTrigger>
            <SelectValue placeholder={t('staff.selectRole')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="admin">{t('staff.roles.admin')}</SelectItem>
            <SelectItem value="reception">{t('staff.roles.reception')}</SelectItem>
            <SelectItem value="workshop">{t('staff.roles.workshop')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!isEditing && (
        <>
          <PasswordInput
            label={t('staff.initialPassword')}
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            showRequirements
            required
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendWelcomeEmail"
              name="sendWelcomeEmail"
              checked={formData.sendWelcomeEmail}
              onChange={handleChange}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            <Label htmlFor="sendWelcomeEmail" className="font-normal">
              {t('staff.sendWelcomeEmail')}
            </Label>
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {isEditing ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  );
}
